import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/common";
import { Building2, Plus, LogOut, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

const IN_STATES = ["Assam", "Delhi", "Maharashtra", "Karnataka", "Tamil Nadu", "West Bengal", "Gujarat", "Uttar Pradesh", "Rajasthan", "Kerala", "Telangana", "Punjab", "Haryana", "Bihar", "Madhya Pradesh", "Odisha", "Other"];

export default function Onboarding() {
  const { businesses, createBusiness, switchBusiness, logout, user } = useAuth();
  const [form, setForm] = useState({ name: "", state: "Assam", gstin: "", pan: "", city: "", address: "" });
  const [loading, setLoading] = useState(false);

  const create = async () => {
    if (!form.name.trim()) return toast.error("Business name is required");
    setLoading(true);
    try { await createBusiness(form); toast.success("Business created"); window.location.href = "/"; }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to create business"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-2xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center"><Building2 className="w-6 h-6 text-white" /></div>
            <div><div className="font-heading text-xl font-bold text-white">RentLedger</div><div className="text-xs text-slate-400">{user?.email}</div></div>
          </div>
          <Button variant="ghost" className="text-slate-300" onClick={() => logout()} data-testid="onboarding-logout"><LogOut className="w-4 h-4 mr-1" /> Sign out</Button>
        </div>

        {businesses.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-5">
            <p className="text-sm font-semibold text-slate-700 mb-3">Choose a business</p>
            <div className="space-y-2">
              {businesses.map((b) => (
                <button key={b.id} data-testid={`pick-business-${b.id}`} onClick={() => switchBusiness(b.id)} className="w-full flex items-center justify-between px-4 py-3 rounded-lg border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 transition">
                  <span className="font-medium text-slate-800">{b.name}</span>
                  <span className="flex items-center gap-2 text-xs text-slate-500 capitalize">{b.role} <ArrowRight className="w-4 h-4" /></span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-xl p-6">
          <p className="font-heading text-lg font-bold text-slate-900 mb-1">Create a business</p>
          <p className="text-sm text-slate-500 mb-4">Each business keeps its own properties, tenants, invoices and team.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Field label="Business Name *"><Input data-testid="biz-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Leo Realty Estates" /></Field></div>
            <Field label="State (for GST)"><select data-testid="biz-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm">{IN_STATES.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <Field label="GSTIN"><Input data-testid="biz-gstin" value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} placeholder="Optional" /></Field>
            <Field label="PAN"><Input value={form.pan} onChange={(e) => setForm({ ...form, pan: e.target.value })} placeholder="Optional" /></Field>
            <Field label="City"><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <div className="md:col-span-2"><Field label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field></div>
          </div>
          <Button data-testid="create-business-btn" onClick={create} disabled={loading} className="w-full mt-5 bg-indigo-600 hover:bg-indigo-700">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Create business</>}</Button>
        </div>
      </div>
    </div>
  );
}
